import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { Header } from "@/components/common/Header";
import { cn } from "@/lib/utils";
import { BookOpen, GraduationCap, LayoutDashboard, Percent, UserPlus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";

const sidebarSections = [
	{
		titleKey: "admin.sidebarMain",
		items: [
			{ href: "/admin", icon: LayoutDashboard, labelKey: "admin.dashboard" },
			{ href: "/admin/courses", icon: BookOpen, labelKey: "admin.courses" },
			{ href: "/admin/students", icon: Users, labelKey: "admin.students" },
		],
	},
	{
		titleKey: "admin.sidebarCommerce",
		items: [
			{ href: "/admin/enrollments/new", icon: UserPlus, labelKey: "admin.enrollments" },
			{ href: "/admin/discounts", icon: Percent, labelKey: "admin.discounts" },
		],
	},
];

const mobileTabItems = [
	{ href: "/admin", icon: LayoutDashboard, labelKey: "admin.dashboard" },
	{ href: "/admin/courses", icon: BookOpen, labelKey: "admin.courses" },
	{ href: "/admin/students", icon: Users, labelKey: "admin.students" },
	{ href: "/admin/discounts", icon: Percent, labelKey: "admin.discounts" },
];

export function AdminLayout() {
	const { t } = useTranslation();
	const location = useLocation();

	function isActive(href: string) {
		return href === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(href);
	}

	return (
		<div className="flex min-h-screen flex-col bg-bg-base">
			<a href="#admin-content" className="skip-to-content">
				{t("nav.skipToContent")}
			</a>
			<Header />
			<div className="flex flex-1">
				{/* Desktop sidebar */}
				<aside className="hidden w-60 flex-shrink-0 border-r border-border-subtle bg-bg-surface p-4 lg:block">
					<div className="mb-4">
						<Link
							to="/"
							className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:text-primary"
						>
							<GraduationCap className="h-4 w-4" />
							{t("nav.backToPlatform")}
						</Link>
					</div>
					{sidebarSections.map((section) => (
						<div key={section.titleKey} className="mb-6">
							<p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted">
								{t(section.titleKey)}
							</p>
							<nav className="flex flex-col gap-1" aria-label={t(section.titleKey)}>
								{section.items.map((item) => {
									const active = isActive(item.href);
									return (
										<Link
											key={item.href}
											to={item.href}
											aria-current={active ? "page" : undefined}
											className={cn(
												"flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
												active
													? "border-l-3 border-accent bg-accent-muted text-accent"
													: "text-secondary hover:bg-bg-hover hover:text-primary",
											)}
										>
											<item.icon className="h-5 w-5" />
											{t(item.labelKey)}
										</Link>
									);
								})}
							</nav>
						</div>
					))}
				</aside>

				<main id="admin-content" className="flex-1 p-4 pb-20 page-enter sm:p-6 lg:p-8 lg:pb-8">
					<ErrorBoundary>
						<Outlet />
					</ErrorBoundary>
				</main>
			</div>

			{/* Mobile bottom tab bar */}
			<nav
				className="fixed bottom-0 left-0 right-0 z-sticky flex items-center justify-around border-t border-border-subtle bg-bg-surface/95 backdrop-blur-md lg:hidden"
				aria-label="Admin navigation"
			>
				{mobileTabItems.map((item) => {
					const active = isActive(item.href);
					return (
						<Link
							key={item.href}
							to={item.href}
							aria-current={active ? "page" : undefined}
							className={cn(
								"flex min-h-[56px] min-w-[56px] flex-col items-center justify-center gap-1 px-2 py-2 text-[11px] font-medium transition-colors",
								active ? "text-accent" : "text-secondary",
							)}
						>
							<item.icon className="h-5 w-5" />
							<span>{t(item.labelKey)}</span>
						</Link>
					);
				})}
			</nav>
		</div>
	);
}
